import React, { useState, useRef, useEffect } from 'react';
import Modal from 'react-bootstrap/lib/Modal';
import Button from 'react-bootstrap/lib/Button';

import { testAccount } from '../../../common/utils';
import Input from '../Input/Input';
import { httpPost, isPasswordValid } from '../../../../utils';

const IDS = {
    USERNAME: 'username',
    OLD_PASSWORD: 'oldPassword',
    PASSWORD: 'password',
    PASSWORD_CONFIRM: 'passwordConfirm'
};

const AccountPopup = ({
    userValue,
    validateDomainName,
    header,
    validateOldPassword,
    testMetadata,
    onSave,
    onClear,
    onCancel,
    passwordConfirmation,
    validatePassword
}) => {
    const [username, setUsername] = useState(userValue);
    const [oldPassword, setOldPassword] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');

    const [inProgress, setInProgress] = useState(false);
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState('');
    const [isClearing, setIsClearing] = useState(false);

    const isExited = useRef(false);

    useEffect(() => {
        setUsername(userValue || '');
    }, [userValue]);

    let timer = null;
    const inputChanged = (id, value) => {
        setError(null);
        if (id === IDS.USERNAME) {
            setUsername(value);
        } else if (id === IDS.OLD_PASSWORD) {
            setOldPassword(value);
        } else if (id === IDS.PASSWORD) {
            setPassword(value);
        } else if (id === IDS.PASSWORD_CONFIRM) {
            setPasswordConfirm(value);
        }
    };

    const testAccountHandler = async () => {
        if (passwordConfirmation && password !== passwordConfirm) {
            return setError('Your password and confirmation password do not match.');
        }

        setError(null);
        setSuccess(null);
        setInProgress(true);

        const testResults = await testAccount(username, password, testMetadata, validateDomainName);
        const testPassed = testResults[0];

        if (isExited.current) {
            return;
        }

        if (!testPassed) {
            const errorMsg = testResults[1];
            setError(errorMsg);
            setInProgress(false);
            return;
        }

        const newUsername = testResults[1];
        if (newUsername) {
            setUsername(newUsername);
        }

        setSuccess('Account Connected Successfully!');
        timer = setTimeout(() => {
            saveHandler(newUsername);
        }, 3000);
    };

    const saveHandler = async (newUsername) => {
        if (isExited.current) {
            return;
        }
        try {
            setInProgress(true);
            setSuccess(null);
            if (passwordConfirmation && password !== passwordConfirm) {
                return setError('Your password and confirmation password do not match.');
            }
            if (validateOldPassword) {
                await httpPost('/settings/validatePassword', {
                    password: oldPassword
                });
            }
            const usernameToSave = newUsername || username;
            if (!usernameToSave && !isClearing) {
                return setError('Validation Error.');
            }
            if (validatePassword && !isPasswordValid(password)) {
                return setError(
                    'The password must contain at least 8 characters and include letters, digits, and special characters.'
                );
            }
            await onSave(usernameToSave, password, oldPassword);
            setIsClearing(false);
        } catch (err) {
            console.error(err.toString());

            if (err.response && err.response.data && err.response.data.error) {
                setError(err.response.data.error);
            } else if (err.data) {
                setError(err.data);
            } else {
                setError('Your old password is wrong.');
            }
        } finally {
            setInProgress(false);
            setIsClearing(false);
        }
    };

    const onExitWithoutSave = (exitFunc) => {
        clearTimeout(timer);
        isExited.current = true;
        if (exitFunc) {
            exitFunc();
        }
    };

    const onClearHandler = () => {
        const hasDataToClear = username || oldPassword || password || passwordConfirm;

        setUsername('');
        setOldPassword('');
        setPassword('');
        setPasswordConfirm('');
        setError('');
        setSuccess(null);
        setIsClearing(true);

        if (onClear && hasDataToClear) {
            onClear();
        }
    };

    return (
        <Modal
            dialogClassName="modal-md"
            id="settings-modal"
            show
            onHide={() => onExitWithoutSave(onCancel)}
            backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Account Credentials</Modal.Title>
                {header && <div className="sub-header-info">{header}</div>}
            </Modal.Header>
            <Modal.Body>
                <div className="form-field">
                    <label className="form-label">Username:</label>
                    <Input id={IDS.USERNAME} value={username} placeholder="Enter username" changed={inputChanged} />
                </div>
                {validateOldPassword && (
                    <div className="form-field">
                        <label className="form-label">Old Password:</label>
                        <Input
                            id={IDS.OLD_PASSWORD}
                            type="password"
                            value={oldPassword}
                            placeholder="Enter old password"
                            changed={inputChanged}
                        />
                    </div>
                )}
                <div className="form-field">
                    <label className="form-label">Password:</label>
                    <Input
                        id={IDS.PASSWORD}
                        type="password"
                        value={password}
                        placeholder="Enter password"
                        changed={inputChanged}
                    />
                </div>
                {passwordConfirmation && (
                    <div className="form-field">
                        <label className="form-label">Password Confirmation:</label>
                        <Input
                            id={IDS.PASSWORD_CONFIRM}
                            type="password"
                            value={passwordConfirm}
                            placeholder="Enter password again"
                            changed={inputChanged}
                        />
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                {onClear && <Button onClick={onClearHandler}>Clear</Button>}
                <Button onClick={() => onExitWithoutSave(onCancel)}>Cancel</Button>
                {testMetadata && (
                    <Button
                        bsStyle="primary"
                        disabled={
                            inProgress ||
                            !username ||
                            (!userValue && passwordConfirmation && password !== passwordConfirm)
                        }
                        onClick={() => testAccountHandler()}>
                        {userValue ? 'Test Existing & Save' : 'Test New & Save'}
                    </Button>
                )}
                {!testMetadata && (
                    <Button bsStyle="success" disabled={inProgress} onClick={() => saveHandler()}>
                        Save
                    </Button>
                )}

                {error && <div className="footer-error">* {error}</div>}
                {success && <div className="footer-success">{success}</div>}
            </Modal.Footer>
        </Modal>
    );
};

export default AccountPopup;
